import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { TableCell } from '@material-ui/core';
import MuiTooltip from '@material-ui/core/Tooltip';

import { SelectableRow } from 'core/components/table/selectable-row';
import { SelectableCell } from 'core/components/table/selectable-cell';
import { AuthContext } from 'core/components/auth';
import { dateTimeFormatterInUserTimezone } from 'core/helpers/formatters';
import { Tooltip } from 'core/components/tooltip';
import useTooltipStyles from 'core/components/tooltip/tooltip.styles';
import helpCircleIcon from 'core/assets/svg/help-circle.svg';
import homeIcon from 'core/assets/svg/ic-home.svg';
import autoIcon from 'core/assets/svg/ic-auto.svg';
import rentersIcon from 'core/assets/svg/ic-renters.svg';
import farmIcon from 'core/assets/svg/ic-farm.svg';
import condoIcon from 'core/assets/svg/ic-condo.svg';
import useStyles from './offer-results.styles';
import { staffOfferLink } from 'core/helpers/bridge-link-opened';
import { abbreviateOfferRep, deriveOfferProducts } from './offer-results.helpers';

const NO_PRICE_TOOLTIP = 'Prices show for offers created on or after Sep 11, 2026. Open this offer to see its price.';

const keepClickOnRow = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const PRODUCT_ICONS = {
  Home: homeIcon,
  Auto: autoIcon,
  Renters: rentersIcon,
  Condo: condoIcon,
  Farm: farmIcon,
  Rental: rentersIcon
};

export const deriveAddress = (address) => {
  const parts = [];
  if (address.address) {
    parts.push(address.address);
  }
  if (address.city) {
    parts.push(address.city);
  }
  if (address.state) {
    parts.push(address.state);
  }
  if (address.zip) {
    parts.push(address.zip);
  }
  return parts.join(', ').replace(/, ([^,]+)$/, ' $1'); // Last part without comma
};

// Cell component to conditionally render TableCell or SelectableCell based on permissions
export const Cell = ({ children, hasPermission, offerLink, classes }) => {
  if (hasPermission) {
    return (
      <SelectableCell to={offerLink} classes={{ root: classes.cell }}>
        {children}
      </SelectableCell>
    );
  }
  return <TableCell classes={{ root: classes.cell }}>{children}</TableCell>;
};

Cell.propTypes = {
  children: PropTypes.node.isRequired,
  hasPermission: PropTypes.bool.isRequired,
  offerLink: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired
};

const OfferItem = ({ offer }) => {
  const classes = useStyles();
  const tooltipClasses = useTooltipStyles({ size: 'large' });
  const history = useHistory();
  const { canQuote, viewOnly } = useContext(AuthContext);

  const onOfferEdit = useCallback(() => {
    const { id } = offer;
    if (canQuote || viewOnly) {
      history.push(`/offer/${id}`);
    }
  }, [offer, canQuote, viewOnly, history]);

  const offerLink = staffOfferLink(offer.id);
  const hasPermission = canQuote || viewOnly;
  const address = deriveAddress(offer.quote.submittedAddress);
  const products = deriveOfferProducts(offer.options);
  const isMissingPrice = products.some((product) => !product.price);

  return (
    <SelectableRow hover id={offer.id} onClick={onOfferEdit} data-cy="row">
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        {[offer.quote.fname, offer.quote.lname].filter(Boolean).join(' ')}
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        <span className={classes.address} title={address}>
          {address}
        </span>
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        <span className={classes.offer}>
          {products.length === 0 && 'N/A'}
          {products.map((product, index) => (
            <React.Fragment key={product.name}>
              {index > 0 && <span className={classes.plus}>+</span>}
              <span className={classes.product}>
                {PRODUCT_ICONS[product.name] ? (
                  // Same bubble as the core Tooltip, which only renders its own small icons.
                  <MuiTooltip title={product.label} placement="top" arrow classes={{ tooltip: tooltipClasses.tooltip }}>
                    <img src={PRODUCT_ICONS[product.name]} alt={product.label} className={classes.productIcon} />
                  </MuiTooltip>
                ) : (
                  product.label
                )}
                {product.price && (
                  <span className={classes.price}>
                    {product.price}
                    <span className={classes.term}>{product.termMonths} mo</span>
                  </span>
                )}
              </span>
            </React.Fragment>
          ))}
          {isMissingPrice && (
            <span className={classes.noPrice}>
              <em>See offer for price</em>
              <span onClick={keepClickOnRow}>
                <Tooltip
                  text={NO_PRICE_TOOLTIP}
                  placement="top"
                  smallIcon={{ src: helpCircleIcon }}
                  iconSize="icon16"
                />
              </span>
            </span>
          )}
        </span>
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        <span className={classes.rep} title={offer.quote.rep || undefined}>
          {abbreviateOfferRep(offer.quote.rep)}
        </span>
      </Cell>
      <Cell hasPermission={hasPermission} offerLink={offerLink} classes={classes}>
        {offer.updatedDateTime && (
          <span className={classes.updated}>
            {dateTimeFormatterInUserTimezone(offer.updatedDateTime, {
              dateOptions: { month: 'short', day: 'numeric' },
              timeOptions: { timeZoneName: 'short' },
              separator: ', '
            })}
          </span>
        )}
      </Cell>
    </SelectableRow>
  );
};

OfferItem.propTypes = {
  offer: PropTypes.object.isRequired
};

export default OfferItem;
